// app/javascript/controllers/rental_booking_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "submitButton", "errorContainer", "datepicker"]

  static values = {
    rentalId: Number,
    startDate: String,
    endDate: String,
    guestCount: Number
  }

  connect() {
    // Initialize date picker if available
    if (this.hasDatepickerTarget) {
      this.initDatepicker()
    }
    
    // Validate CSRF token on connect
    this.validateCsrfToken()
  }
  
  // Validate that we have a valid CSRF token
  validateCsrfToken() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')
    if (!csrfToken || !csrfToken.content) {
      console.warn('CSRF token missing or invalid, attempting to refresh')
      
      // In production, we'll log this to Honeybadger
      if (window.Honeybadger && window.railsEnvironment === 'production') {
        window.Honeybadger.notify({
          name: 'CSRFTokenMissing',
          message: 'CSRF token is missing or invalid on page load',
          context: {
            url: window.location.href,
            controller: 'RentalBookingController'
          }
        })
      }
      
      // If we're in a Turbo-enabled app, try to refresh the token
      if (typeof Turbo !== 'undefined') {
        // Fetch a fresh CSRF token by making a GET request to the current page
        fetch(window.location.href, {
          method: 'GET',
          headers: {
            'Accept': 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        })
        .then(response => response.text())
        .then(html => {
          // Create a temporary DOM element to parse the HTML
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = html
          
          // Extract the CSRF token
          const newCsrfToken = tempDiv.querySelector('meta[name="csrf-token"]')
          if (newCsrfToken && newCsrfToken.content) {
            // Create or update the CSRF meta tag
            if (csrfToken) {
              csrfToken.content = newCsrfToken.content
            } else {
              const meta = document.createElement('meta')
              meta.name = 'csrf-token'
              meta.content = newCsrfToken.content
              document.head.appendChild(meta)
            }
          }
        })
        .catch(error => {
          console.error('Failed to refresh CSRF token:', error)
          if (window.Honeybadger) {
            window.Honeybadger.notify(error, {
              context: {
                action: 'refreshCsrfToken',
                controller: 'RentalBookingController'
              }
            })
          }
        })
      }
    }
  }

  // This method is called when the form is submitted
  createIntent(event) {
    event.preventDefault()
    
    // Get the form and CSRF token
    if (!this.hasFormTarget) {
      console.error('Form target not found')
      return
    }
    
    const form = this.formTarget
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    
    // Validate CSRF token before submission
    if (!csrfToken) {
      console.error('CSRF token missing, cannot submit form')
      
      if (this.hasErrorContainerTarget) {
        this.errorContainerTarget.innerHTML = `
          <div class="p-4 text-red-800 border-2 border-red-300 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 dark:border-red-800" role="alert">
            <div class="flex items-center">
              <svg class="flex-shrink-0 w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
              <h3 class="text-lg font-medium">Security Error</h3>
            </div>
            <div class="mt-2 text-sm">Session validation failed. Please refresh the page and try again.</div>
            <div class="mt-4">
              <button onclick="window.location.reload()" class="inline-flex items-center px-4 py-2 text-sm font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                Refresh Page
              </button>
            </div>
          </div>
        `
        this.errorContainerTarget.classList.remove('hidden')
      }
      
      // Report to Honeybadger
      if (window.Honeybadger) {
        window.Honeybadger.notify({
          name: 'CSRFTokenMissing',
          message: 'CSRF token missing during form submission',
          context: {
            controller: 'RentalBookingController',
            action: 'createIntent',
            url: window.location.href
          }
        })
      }
      
      return
    }
    
    // Save original button text
    let originalButtonText = ''
    if (this.hasSubmitButtonTarget) {
      originalButtonText = this.submitButtonTarget.innerHTML
      this.submitButtonTarget.innerHTML = '<svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Processing...'
      this.submitButtonTarget.disabled = true
    }
    
    // Hide any previous error messages
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.add('hidden')
      this.errorContainerTarget.innerHTML = ''
    }
    
    // Prepare the reservation data
    const formData = new FormData(form)
    const reservationData = {
      rental_id: this.rentalIdValue || formData.get('reservation[rental_id]'),
      start_date: this.startDateValue || formData.get('reservation[start_date]'),
      end_date: this.endDateValue || formData.get('reservation[end_date]'),
      guest_count: this.guestCountValue || formData.get('reservation[guest_count]')
    }
    
    // Log the data being sent in development
    if (!this.isProduction()) {
      console.log('Sending reservation data:', reservationData)
    }

    const fullstorySessionUrl = window.FS?.getCurrentSessionURL?.(true)

    // First create the reservation intent
    fetch('/customer/reservations/create_intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        reservation: reservationData,
        fullstory_session_url: fullstorySessionUrl
      }),
      credentials: 'same-origin'
    })
    .then(response => this.#parseJsonOrThrow(response))
    .then(data => {
      if (data && data.success) {
        const { rental_id, start_date, end_date, guest_count } = reservationData
        window.location.href = `/customer/reservations/new?rental_id=${rental_id}&start_date=${start_date}&end_date=${end_date}&guest_count=${guest_count}&intent_id=${data.intent_id}`
      } else {
        this.#showError(data?.error || 'An unexpected error occurred. Please try again.')

        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.innerHTML = originalButtonText
          this.submitButtonTarget.disabled = false
        }
      }
    })
    .catch(error => {
      if (error.handled) return

      console.error('Error:', error)

      if (window.Honeybadger) {
        const statusBucket = !error.status ? 'network' : error.status >= 500 ? '5xx' : '4xx'
        window.Honeybadger.notify(error, {
          context: {
            component: 'RentalBookingController',
            action: 'createIntent',
            environment: window.railsEnvironment,
            userAgent: navigator.userAgent,
            url: window.location.href,
            formData: reservationData,
            csrfTokenPresent: !!csrfToken,
            httpStatus: error.status
          },
          fingerprint: `booking-error-${statusBucket}-${reservationData.rental_id}`
        })
      }

      if (this.hasErrorContainerTarget) {
        if (error.message.includes('sign in') || error.message.includes('login')) {
          this.errorContainerTarget.innerHTML = `
            <div class="p-4 text-blue-800 border-2 border-blue-300 rounded-lg bg-blue-50 dark:bg-gray-800 dark:text-blue-400 dark:border-blue-800" role="alert">
              <div class="flex items-center">
                <svg class="flex-shrink-0 w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
                <h3 class="text-lg font-medium">Authentication Required</h3>
              </div>
              <div class="mt-2 text-sm">${error.message}</div>
              <div class="mt-4">
                <a href="/users/sign_in?redirect_to=${encodeURIComponent(window.location.href)}" class="inline-flex items-center px-4 py-2 text-sm font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                  Sign In
                </a>
              </div>
            </div>
          `
        } else {
          this.#showError(error.message || 'An unexpected error occurred. Please try again.')
        }
        this.errorContainerTarget.classList.remove('hidden')
      }

      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.innerHTML = originalButtonText
        this.submitButtonTarget.disabled = false
      }
    })
  }

  addToBooking(event) {
    event.preventDefault()

    if (!this.hasFormTarget) {
      console.error('Form target not found')
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    if (!csrfToken) {
      this.#showError('Session validation failed. Please refresh the page and try again.')

      if (window.Honeybadger) {
        window.Honeybadger.notify({
          name: 'CSRFTokenMissing',
          message: 'CSRF token missing during addToBooking submission',
          context: { controller: 'RentalBookingController', action: 'addToBooking', url: window.location.href }
        })
      }
      return
    }

    const form = this.formTarget
    const formData = new FormData(form)
    const reservationData = {
      rental_id: this.rentalIdValue || formData.get('reservation[rental_id]'),
      start_date: this.startDateValue || formData.get('reservation[start_date]'),
      end_date: this.endDateValue || formData.get('reservation[end_date]'),
      guest_count: this.guestCountValue || formData.get('reservation[guest_count]')
    }

    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.add('hidden')
      this.errorContainerTarget.innerHTML = ''
    }

    fetch('/customer/booking_cart/add_line', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ reservation: reservationData }),
      credentials: 'same-origin'
    })
    .then(response => {
      if (response.status === 422) {
        return response.json().then(data => {
          if (data && data.code === 'date_conflict') {
            this.#showError(
              `${data.error} ` +
              `<a href="/customer/booking_cart" class="font-semibold underline">Review your cart →</a>`
            )
          } else {
            this.#showError(data?.error || 'Unable to add to booking — please try again.')
          }
          // Sentinel: skip Honeybadger; UI already showed the error
          const handled = new Error('handled')
          handled.handled = true
          throw handled
        })
      }

      return this.#parseJsonOrThrow(response)
    })
    .then(data => {
      if (data && data.success && data.redirect_url) {
        window.location.assign(data.redirect_url)
      } else {
        this.#showError(data?.error || 'Unable to add to booking — please try again.')
      }
    })
    .catch(error => {
      if (error.handled) return

      console.error('Error:', error)

      if (window.Honeybadger) {
        const statusBucket = !error.status ? 'network' : error.status >= 500 ? '5xx' : '4xx'
        window.Honeybadger.notify(error, {
          context: {
            component: 'RentalBookingController',
            action: 'addToBooking',
            environment: window.railsEnvironment,
            url: window.location.href,
            formData: reservationData,
            csrfTokenPresent: !!csrfToken,
            httpStatus: error.status
          },
          fingerprint: `add-to-booking-error-${statusBucket}-${reservationData.rental_id}`
        })
      }

      this.#showError(error.message || 'Unable to add to booking — please try again.')
    })
  }

  // Parses a non-422 fetch response as JSON.
  // Follows redirect_url if present in an error body; throws a status-annotated
  // Error for other failures so the catch can report to Honeybadger.
  #parseJsonOrThrow(response) {
    if (response.ok) return response.json()

    return response.json().then(data => {
      if (response.status === 401) {
        const e = new Error('Please sign in to continue with your booking')
        e.status = 401
        throw e
      }

      if (data?.redirect_url) {
        window.location.assign(data.redirect_url)
        // Sentinel: skip Honeybadger; navigation is already in flight
        const handled = new Error('handled')
        handled.handled = true
        throw handled
      }

      const e = new Error(data?.error || `HTTP error! Status: ${response.status}`)
      e.status = response.status
      throw e
    }).catch(e => {
      if (e instanceof SyntaxError) throw new Error(`HTTP error! Status: ${response.status}`)
      throw e
    })
  }

  #showError(html) {
    if (!this.hasErrorContainerTarget) return
    this.errorContainerTarget.innerHTML = `
      <div class="p-4 text-red-800 border-2 border-red-300 rounded-lg bg-red-50" role="alert">
        <div class="flex items-start gap-2">
          <svg class="flex-shrink-0 w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
          <div class="text-sm">${html}</div>
        </div>
      </div>
    `
    this.errorContainerTarget.classList.remove('hidden')
  }

  isProduction() {
    return window.railsEnvironment === 'production'
  }
}
